import type { ITrustCenterStandardName } from '@/lib/constants/standards'

export const PUBLIC_TRUST_CENTER = {
  TITLE: 'Trust Center',
  PAGE_TITLE: (organization: string) => `Trust Center de ${organization}`,
  NAV: {
    CERTIFICATIONS: 'Certificaciones',
    CONTROLS: 'Controles',
    DOCUMENTS: 'Documentos',
    SUBPROCESSORS: 'Subprocesadores',
    REQUEST_ACCESS: 'Solicitar acceso',
    POWERED_BY: 'Con la tecnología de',
  },
  FOOTER: {
    COPYRIGHT: (year: number, organization: string) => `© ${year} ${organization}. Todos los derechos reservados.`,
  },
  OVERVIEW: {
    LAST_UPDATED: (date: string) => `Última actualización ${date}`,
    MONITORING: (controls: number, domains: number) => `${controls} controles monitorizados en ${domains} dominios`,
    STATS: {
      CERTIFICATIONS: 'Certificaciones',
      CONTROLS: 'Controles monitorizados',
      DOCUMENTS: 'Documentos',
      SUBPROCESSORS: 'Subprocesadores',
    },
    CERTIFICATIONS: {
      TITLE: 'Certificaciones y marcos',
      VIEW_DETAILS: 'Ver detalles',
    },
    CONTROLS: {
      TITLE: 'Controles de seguridad',
      SUBTITLE: (count: number) => `${count} controles monitorizados de forma continua`,
      VIEW_ALL: 'Ver todos los controles',
    },
    DOCUMENTS: {
      TITLE: 'Documentos',
      SUBTITLE: 'Documentos confidenciales bajo NDA',
      VIEW_ALL: 'Ver todos los documentos',
    },
    SUBPROCESSORS: {
      TITLE: 'Subprocesadores',
      SUBTITLE: 'Terceros que pueden tratar datos de clientes',
      VIEW_ALL: 'Ver todos',
    },
    CONTACTS: {
      VULNERABILITY: {
        TITLE: 'Reportar una vulnerabilidad',
        BODY: 'Divulgación coordinada con acuse de recibo en 48 horas.',
      },
      QUESTIONNAIRES: {
        TITLE: 'Cuestionarios de seguridad',
        BODY: 'Los documentos anteriores responden a la mayoría de preguntas. Envíanos lo que quede pendiente.',
      },
    },
  },
  CERTIFICATIONS: {
    TITLE: 'Certificaciones y marcos',
    CERTIFICATE: 'Certificado',
    EMPTY: 'Aún no hay certificaciones',
    STATUS: {
      CERTIFIED: 'Certificado',
    },
    DESCRIPTIONS: {
      'ISO 27001': 'Gestión de la seguridad de la información',
      DORA: 'Resiliencia operativa digital',
      ENS: 'Esquema Nacional de Seguridad',
      'PCI DSS': 'Seguridad de datos de tarjetas de pago',
      'ISO 9001': 'Gestión de la calidad',
      NIS2: 'Seguridad de redes y sistemas de la UE',
      SOC2: 'Seguridad, disponibilidad, confidencialidad',
      'ISO 42001': 'Sistema de gestión de IA',
      RGPD: 'Reglamento general de protección de datos',
      'ISO 14001': 'Gestión ambiental',
      'ISO 27017': 'Seguridad de servicios en la nube',
      'ISO 27018': 'Privacidad en la nube (protección de datos personales)',
    } satisfies Record<ITrustCenterStandardName, string>,
    SUMMARIES: {
      'ISO 27001':
        'Controles basados en riesgos que preservan la confidencialidad, integridad y disponibilidad de la información',
      DORA: 'Resiliencia de los sistemas TIC que soportan servicios financieros en la UE',
      ENS: 'Requisitos de seguridad para sistemas que prestan servicio al sector público español',
      'PCI DSS': 'Protección de los datos de titulares de tarjetas en su almacenamiento, tratamiento y transmisión',
      'ISO 9001': 'Procesos consistentes y orientados al cliente en mejora continua',
      NIS2: 'Gestión de riesgos de ciberseguridad y notificación de incidentes para servicios esenciales',
      SOC2: 'Garantía independiente sobre los controles de seguridad, disponibilidad y confidencialidad',
      'ISO 42001': 'Desarrollo y uso responsable de la inteligencia artificial',
      RGPD: 'Tratamiento lícito y transparente de datos personales conforme a la normativa europea',
      'ISO 14001': 'Gestión sistemática y reducción del impacto ambiental',
      'ISO 27017': 'Controles de seguridad específicos para la prestación y el uso de servicios en la nube',
      'ISO 27018': 'Protección de los datos personales tratados en servicios de nube pública',
    } satisfies Record<ITrustCenterStandardName, string>,
  },
  CONTROLS: {
    TITLE: 'Controles de seguridad',
    SUBTITLE: (controls: number, domains: number) => `${controls} controles en ${domains} dominios`,
    COUNT: (count: number) => `${count} controles`,
    SEARCH_PLACEHOLDER: 'Buscar controles',
    SHOWING_ALL: (total: number) => `Mostrando los ${total}`,
    SHOWING: (shown: number, total: number) => `Mostrando ${shown} de ${total}`,
    EMPTY: 'Ningún control coincide con la búsqueda',
    ID: 'ID',
    CONTROL: 'Control',
    DOMAIN: 'Dominio',
    REQUEST_EVIDENCE: 'Solicitar evidencias',
    EVIDENCE_RESOURCE: (domain: string) => `Evidencias de ${domain}`,
    OTHER_DOMAINS: 'Otros dominios',
  },
  RESOURCES: {
    TITLE: 'Documentos',
    SUBTITLE: 'Los documentos públicos se descargan directamente. Los confidenciales se facilitan tras firmar un NDA.',
    FILTER_ALL: 'Todos',
    FILTER_PUBLIC: 'Públicos',
    FILTER_PRIVATE: 'Bajo NDA',
    PUBLIC: 'Público',
    PRIVATE: 'Bajo NDA',
    DOWNLOAD: 'Descargar',
    REQUEST: 'Solicitar',
    EMPTY: 'No hay documentos',
  },
  ERROR: {
    SOMETHING_WENT_WRONG: '¡Oops! Algo salió mal',
    TITLE: 'Error al cargar los recursos públicos',
    DESCRIPTION: 'No pudimos cargar los recursos públicos. Esto puede ser temporal, por favor inténtalo de ' + 'nuevo.',
    RETRY: 'Intentar de nuevo',
    WRONG_EMAIL: 'Formato de email inválido',
  },
  SUBPROCESSORS: {
    TITLE: 'Subprocesadores y privacidad',
    SUBTITLE: 'Terceros que pueden tratar datos de clientes y cómo se tratan esos datos',
    PROVIDER: 'Proveedor',
    PURPOSE: 'Finalidad',
    LOCATION: 'Ubicación',
    EMPTY: 'No hay subprocesadores',
    PRIVACY: {
      RESIDENCY: {
        TITLE: 'Residencia de datos en la UE',
        BODY: 'Los datos de clientes se almacenan y tratan en regiones de la UE. Sin transferencias fuera del EEE sin CCT.',
      },
      RETENTION: {
        TITLE: 'Retención y eliminación',
        BODY: 'Los datos se eliminan tras la finalización del contrato y las copias de seguridad caducan según nuestra política de retención.',
      },
      RIGHTS: {
        TITLE: 'Derechos de los interesados',
        BODY: 'Las solicitudes de acceso, rectificación y supresión se responden en un plazo de 30 días.',
      },
      GDPR: {
        TITLE: 'GDPR',
        BODY: 'DPA disponible bajo solicitud, con la lista actual de subprocesadores adjunta.',
      },
    },
  },
  REQUEST_ACCESS: {
    TITLE: 'Solicitar acceso a documentos',
    DESCRIPTION: 'Los documentos confidenciales se comparten tras revisar tu solicitud.',
    FULL_NAME: 'Nombre completo',
    FULL_NAME_PLACEHOLDER: 'Ana García',
    EMAIL: 'Email corporativo',
    EMAIL_PLACEHOLDER: 'ana@empresa.com',
    COMPANY: 'Empresa',
    COMPANY_PLACEHOLDER: 'Empresa S.L.',
    DOCUMENTS: 'Documentos solicitados',
    REASON: 'Motivo de la solicitud',
    REASON_PLACEHOLDER: 'Revisión de seguridad del proveedor antes de renovar el contrato',
    REQUIRED: 'Nombre, email y un documento obligatorios',
    READY: 'Respondemos en un día laborable',
    CANCEL: 'Cancelar',
    SUBMIT: 'Enviar solicitud',
    SENT_TITLE: 'Solicitud enviada',
    SENT_DESCRIPTION: 'El equipo revisará tu solicitud y te responderá por email.',
    CLOSE: 'Cerrar',
  },
  ACCESS_REQUEST_EMAIL: {
    SUBJECT: 'Nueva solicitud de acceso en vuestro Trust Center',
    GREETING: 'Hola,',
    INTRO: 'Se ha solicitado acceso a documentos de vuestro Trust Center. Estos son los detalles:',
    NAME: 'Nombre',
    EMAIL: 'Email',
    COMPANY: 'Empresa',
    DOCUMENTS: 'Documentos',
    REASON: 'Motivo',
    NEXT_STEPS: 'Podéis responderle directamente a su email.',
    LINKS_NOTE: 'Los documentos con enlace aparecen arriba.',
    SIGNATURE: 'Un saludo,\nEl equipo de Qalea',
  },
} as const
